import {
    useCertificationsQuery,
    useCompaniesQuery,
    useCountriesQuery,
    useDataCategoriesQuery,
    useDataSubjectsQuery,
    useDocumentQuery,
    useGroupCompaniesQuery,
    usePoliciesQuery,
    useSecurityMeasuresQuery,
    useSelectableAllLegalBasesAllQuery,
    useSelectableBusinessAreaQuery,
    useSelectableDeletionPeriodsQuery,
    useSelectableDocumentTypesQueryByUsageType,
    useSelectableLegalEntityTypesQuery,
    useSelectableProcessingActivitiesQuery,
    useSelectablePurposesQuery,
    useSelectableSystemsQuery,
    useSelectableTagsQuery,
    useSelectableUsersQuery,
    useSystemDocumentQuery,
    useTransferBasesQuery,
} from "../../common/hooks/useSelectableItemQueries";
import { DataSourceEnum, SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { useTranslation } from "../../localization/useTranslation";
import { ControllerProcess, SharingTypeEnum } from "../../processingActivity/processingActivityOverview/ProcessingActivityOverview.types";
import { DistributionArea } from "../../processingActivity/systemsStep/SystemsStep.types";
import { AnnualWheelPriority } from "../../annualWheel/annualWheelDialog/AnnualWheelDialog.types";
import { LevelOfProtectionType } from "../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { LegalBasisSelectOption } from "../../processingActivity/legalBasesStep/LegalBasesStep.types";
import { ProcessingActivityStatus } from "../../processingActivity/processingActivities/ProcessingActivities.types";
import { PropertyType } from "../../common/PropertyTypes";

import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { SystemStatus } from "../../system/systemEdit/SystemEdit.types";
import { dataTypeOptions, entityTypeOptions, storageTypeOptions } from "../../system/systemEnumOptions";
import { legalEntityAuditFrequencyOptions, legalEntityAuditTypeOptions } from "../../legalEntity/legalEntityAuditOptions";
import { getNoOfDataSubjectSelectOptions, getNoOfProcessorsSelectOptions } from "../../processingActivity/dataSubjectStep/DataSubjectStep.hooks";
import { DocumentTypeUsageType } from "../../masterData/documentType/documentTypes/DocumentTypes.types";

export function useColumnTypeContextQueryProvider(type: PropertyType) {
    const { translateString, translateCountry, translateStatus, translateDataSubjectsEnum, translateProcessorsEnum } = useTranslation();
    const { addonsFeatures, platformFeatures } = usePlanContext();

    let purposeQuery = useSelectablePurposesQuery(type === PropertyType.Purposes);
    let deletionPeriodsQuery = useSelectableDeletionPeriodsQuery(type === PropertyType.DeletionPeriods);
    let processingActivityQuery = useSelectableProcessingActivitiesQuery(type === PropertyType.ProcessingActivities);
    let systemsQuery = useSelectableSystemsQuery(type === PropertyType.Systems);
    let businessAreasQuery = useSelectableBusinessAreaQuery(type === PropertyType.BusinessAreas);
    let responsiblesQuery = useSelectableUsersQuery(enableUserQuery(type));
    let dataCategoriesQuery = useDataCategoriesQuery(type === PropertyType.DataCategories);
    let dataSubjectsQuery = useDataSubjectsQuery(type === PropertyType.DataSubjects);
    let transferBasesQuery = useTransferBasesQuery(type === PropertyType.TransferBases);
    let securityMeasuresQuery = useSecurityMeasuresQuery(type === PropertyType.SecurityMeasures, true);
    let companiesQuery = useCompaniesQuery(enableCompanyQuery(type));
    let groupCompaniesQuery = useGroupCompaniesQuery(enableGroupCompanyQuery(type));
    let documentQuery = useDocumentQuery(type === PropertyType.Documents || type === PropertyType.Agreement);
    let documentTiaQuery = useDocumentQuery(type === PropertyType.TIA, DocumentTypeUsageType.Tia);
    let documentDataProcessorAgreementQuery = useDocumentQuery(type === PropertyType.DataProcessorAgreement, DocumentTypeUsageType.DataProcessorTransfer);
    let countriesQuery = useCountriesQuery(enableCountryQuery(type));
    let allLegalBasesQuery = useSelectableAllLegalBasesAllQuery(enableLegalBasisQuery(type));
    let policiesQuery = usePoliciesQuery(type === PropertyType.Policies);
    let systemDocumentsQuery = useSystemDocumentQuery(type === PropertyType.SystemDocuments);
    let certificationsQuery = useCertificationsQuery(type === PropertyType.Certifications);
    let tagsQuery = useSelectableTagsQuery(platformFeatures.tags);
    let documentTypesQuery = useSelectableDocumentTypesQueryByUsageType(DocumentTypeUsageType.AllTransfers, type === PropertyType.DocumentTypes);
    let legalEntityTypesQuery = useSelectableLegalEntityTypesQuery(type === PropertyType.Type);

    const getListOptions = () => {
        if (type === PropertyType.Purposes) return purposeQuery;
        else if (type === PropertyType.DeletionPeriods) return deletionPeriodsQuery;
        else if (type === PropertyType.LegalBases)
            return { data: getLegalBases(allLegalBasesQuery.data?.directives, allLegalBasesQuery.data?.doubleLegalBases, allLegalBasesQuery.isLoading) };
        else if (type === PropertyType.ProcessingActivities) return processingActivityQuery;
        else if (type === PropertyType.Systems) return systemsQuery;
        else if (type === PropertyType.BusinessAreas) return businessAreasQuery;
        else if (enableUserQuery(type)) return responsiblesQuery;
        else if (type === PropertyType.DataCategories) return dataCategoriesQuery;
        else if (type === PropertyType.DataSubjects) return dataSubjectsQuery;
        else if (type === PropertyType.DisclosureNationalLaws || type === PropertyType.NationalLaws)
            return { data: allLegalBasesQuery.data?.nationalLaws, isLoading: allLegalBasesQuery.isLoading };
        else if (type === PropertyType.TransferBases) return transferBasesQuery;
        else if (type === PropertyType.SecurityMeasures) return securityMeasuresQuery;
        else if (enableCompanyQuery(type)) return companiesQuery;
        else if (enableGroupCompanyQuery(type)) return groupCompaniesQuery;
        else if (type === PropertyType.TIA) return { data: getDocuments(documentTiaQuery.data), isLoading: documentTiaQuery.isLoading };
        else if (type === PropertyType.Documents) return { data: documentQuery.data, isLoading: documentQuery.isLoading };
        else if (type === PropertyType.DataProcessorAgreement)
            return { data: getDocuments(documentDataProcessorAgreementQuery.data), isLoading: documentDataProcessorAgreementQuery.isLoading };
        else if (enableCountryQuery(type)) {
            return {
                data: countriesQuery.data?.map((x) => {
                    return { name: translateCountry(x.countryCode), id: x.countryCode };
                }),
                isLoading: countriesQuery.isLoading,
            };
        } else if (type === PropertyType.SharingTypes) return { data: getSharingTypes(translateString), isLoading: false };
        else if (type === PropertyType.SystemTypes) return { data: getSystemTypes(translateString), isLoading: false };
        else if (type === PropertyType.RecipientRoles || type === PropertyType.SenderRoles || type === PropertyType.Roles)
            return { data: getSharingRoles(type, translateString), isLoading: false };
        else if (type === PropertyType.Risk || type === PropertyType.ProcessingActivityClassification)
            return { data: getProcessingActivityRisks(type, translateString), isLoading: false };
        else if (type === PropertyType.ReceivingLocation || type === PropertyType.DataProcessorLocations || type === PropertyType.Location)
            return { data: getLocations(translateString), isLoading: false };
        else if (type === PropertyType.DisclosureLegalBases) return { data: allLegalBasesQuery.data?.directives, isLoading: allLegalBasesQuery.isLoading };
        else if (type === PropertyType.DisclosureDoubleLegalBases)
            return { data: allLegalBasesQuery.data?.doubleLegalBases, isLoading: allLegalBasesQuery.isLoading };
        else if (type === PropertyType.Agreement)
            return {
                data: getDocuments(documentQuery.data),
                isLoading: documentQuery.isLoading,
            };
        else if (type === PropertyType.Type) {
            return legalEntityTypesQuery;
        } else if (type === PropertyType.ProcessingActivityStatus) return { data: getStatusTypes(translateStatus, addonsFeatures.research), isLoading: false };
        else if (type === PropertyType.DocumentTypes) return documentTypesQuery;
        else if (type === PropertyType.LegalEntitySupervisionType) return { data: legalEntityAuditTypeOptions(translateString), isLoading: false };
        else if (type === PropertyType.LegalEntitySupervisionFrequency) return { data: legalEntityAuditFrequencyOptions(translateString), isLoading: false };
        else if (type === PropertyType.Policies) return { data: policiesQuery.data, isLoading: policiesQuery.isLoading };
        else if (type === PropertyType.SystemStatus) return { data: getSystemStatusTypes(translateString), isLoading: false };
        else if (type === PropertyType.SystemDocuments) return { data: systemDocumentsQuery.data, isLoading: systemDocumentsQuery.isLoading };
        else if (type === PropertyType.SystemStorageType) return { data: storageTypeOptions(translateString), isLoading: false };
        else if (type === PropertyType.SystemEntityType) return { data: entityTypeOptions(translateString), isLoading: false };
        else if (type === PropertyType.SystemDataType) return { data: dataTypeOptions(translateString), isLoading: false };
        else if (type === PropertyType.Certifications) return { data: certificationsQuery.data, isLoading: certificationsQuery.isLoading };
        else if (type === PropertyType.DataSource) return { data: getDataSources(translateString) };
        else if (type === PropertyType.NumberOfDataSubjects) return { data: getNoOfDataSubjectSelectOptions(translateDataSubjectsEnum) };
        else if (type === PropertyType.NumberOfProcessors) return { data: getNoOfProcessorsSelectOptions(translateProcessorsEnum) };
        else if (type === PropertyType.Tags) return tagsQuery;
    };

    return {
        getListOptions,
    };

    function getDocuments(data: SelectableItem[] | undefined) {
        if (data && !data.some((x) => x.name === translateString("no"))) {
            data.push({ name: translateString("no"), id: "False" });
            data.push({ name: translateString("yes"), id: "True" });
        }
        return data;
    }

    function getLegalBases(directives: LegalBasisSelectOption[] | undefined, doubleLegalBases: LegalBasisSelectOption[] | undefined, isLoading: boolean) {
        if (!isLoading) {
            return directives!.concat(doubleLegalBases!);
        }

        return [];
    }

    function enableUserQuery(type: PropertyType) {
        return type === PropertyType.Owners || type === PropertyType.ValidatedBy || type === PropertyType.Responsibles || type === PropertyType.DocumentOwners;
    }

    function enableCompanyQuery(type: PropertyType) {
        return (
            type === PropertyType.DataProcessors ||
            type === PropertyType.Senders ||
            type === PropertyType.Recipients ||
            type === PropertyType.Sources ||
            type === PropertyType.Disclosures ||
            type === PropertyType.DataControllers ||
            type === PropertyType.SecondarySuppliers ||
            type === PropertyType.DataProcessorNames ||
            type === PropertyType.LegalEntities ||
            type === PropertyType.JointDataControllers
        );
    }

    function enableGroupCompanyQuery(type: PropertyType) {
        return (
            type === PropertyType.GroupCompanies ||
            type === PropertyType.GroupEntityDataControllers ||
            type === PropertyType.GroupEntitiesOnDocuments ||
            type === PropertyType.InternalContractParties
        );
    }

    function enableCountryQuery(type: PropertyType) {
        return (
            type === PropertyType.RecipientCountries ||
            type === PropertyType.SenderCountries ||
            type === PropertyType.StorageLocations ||
            type === PropertyType.SupportAccessLocations ||
            type === PropertyType.Country ||
            type === PropertyType.DataProcessorCountries
        );
    }

    function enableLegalBasisQuery(type: PropertyType) {
        return (
            type === PropertyType.LegalBases ||
            type === PropertyType.DisclosureDoubleLegalBases ||
            type === PropertyType.DisclosureLegalBases ||
            type === PropertyType.DisclosureNationalLaws ||
            type === PropertyType.NationalLaws
        );
    }
}

export function getSharingRoles(type: PropertyType, translateString: (key: string, interpolation?: any) => string) {
    const sharingRoles = [
        { id: ControllerProcess.Controller.toString(), name: translateString("Controller") },
        { id: ControllerProcess.ProcessorW.toString(), name: translateString("ProcessorW") },
        { id: ControllerProcess.JointControllers.toString(), name: translateString("JointControllers") },
    ];

    if (type === PropertyType.RecipientRoles || type === PropertyType.Roles)
        sharingRoles.push({ id: ControllerProcess.Subprocessor.toString(), name: translateString("Subprocessor") });

    return sharingRoles;
}

export function getSharingTypes(translateString: (key: string, interpolation?: any) => string) {
    return [
        { name: translateString("dataProcessors"), id: SharingTypeEnum.dataProcessor.toString() },
        { name: translateString("dataControllers"), id: SharingTypeEnum.dataController.toString() },
        { name: translateString("disclosures"), id: SharingTypeEnum.disclosure.toString() },
        { name: translateString("sources"), id: SharingTypeEnum.source.toString() },
        { name: translateString("JointControllers"), id: SharingTypeEnum.jointDataController.toString() },
    ];
}

export function getStatusTypes(translateStatus: (status: string) => string, hasCaseManagementEnabled: boolean) {
    var status = [
        { name: translateStatus(ProcessingActivityStatus.Active.toString()), id: ProcessingActivityStatus.Active.toString() },
        { name: translateStatus(ProcessingActivityStatus.Draft.toString()), id: ProcessingActivityStatus.Draft.toString() },
        { name: translateStatus(ProcessingActivityStatus.Archived.toString()), id: ProcessingActivityStatus.Archived.toString() },
    ];

    if (hasCaseManagementEnabled) {
        status.push({ name: translateStatus(ProcessingActivityStatus.DraftSubmitted.toString()), id: ProcessingActivityStatus.DraftSubmitted.toString() });
        status.push({
            name: translateStatus(ProcessingActivityStatus.AwaitingResearcher.toString()),
            id: ProcessingActivityStatus.AwaitingResearcher.toString(),
        });
        status.push({ name: translateStatus(ProcessingActivityStatus.Cancelled.toString()), id: ProcessingActivityStatus.Cancelled.toString() });
    }

    return status;
}

export function getLocations(translateString: (key: string, interpolation?: any) => string) {
    return [
        { id: LevelOfProtectionType.EU.toString(), name: translateString("eu") },
        { id: LevelOfProtectionType.EEA.toString(), name: translateString("eea") },
        { id: LevelOfProtectionType.AdequateProtection.toString(), name: translateString("secureThirdCountries") },
        { id: LevelOfProtectionType.ThirdCountry.toString(), name: translateString("unsecureThirdCountries") },
        { id: LevelOfProtectionType.DataPrivacyFramework.toString(), name: translateString("unsecureThirdCountries") },
    ];
}

export function getSystemTypes(translateString: (key: string, interpolation?: any) => string) {
    return [
        { id: DistributionArea.GlobalSystem.toString(), name: translateString("globalSystem") },
        { id: DistributionArea.LocalSystem.toString(), name: translateString("localSystem") },
        { id: DistributionArea.PhysicalArchive.toString(), name: translateString("physicalArchive") },
    ];
}

export function getProcessingActivityRisks(type: PropertyType, translateString: (key: string, interpolation?: any) => string) {
    return [
        { id: "0", name: type === PropertyType.ProcessingActivityClassification ? translateString("noData") : translateString("notAnswered") },
        { id: AnnualWheelPriority.VeryLow.toString(), name: translateString("veryLow") },
        { id: AnnualWheelPriority.Low.toString(), name: translateString("low") },
        { id: AnnualWheelPriority.Moderate.toString(), name: translateString("moderate") },
        { id: AnnualWheelPriority.High.toString(), name: translateString("high") },
        { id: AnnualWheelPriority.VeryHigh.toString(), name: translateString("veryHigh") },
    ];
}

export function getSystemStatusTypes(translateString: (key: string, interpolation?: any) => string) {
    return [
        { name: translateString("active"), id: SystemStatus.Active.toString() },
        { name: translateString("underImplementation"), id: SystemStatus.UnderImplementation.toString() },
        { name: translateString("phasedOut"), id: SystemStatus.PhasedOut.toString() },
    ];
}

export function getDataSources(translateString: (key: string, interpolation?: any) => string) {
    return [
        { id: DataSourceEnum.TheDataSubject.toString(), name: translateString("theDataSubject") },
        { id: DataSourceEnum.ExternalDataSource.toString(), name: translateString("externalDataSource") },
    ];
}
